import React, { useEffect, useRef } from "react";
import { AlertCircle, X } from "lucide-react";

/**
 * Eye-catching error banner designed to sit RIGHT NEXT to the Save / submit
 * button in any form. Solves the "I clicked Save but nothing happened"
 * problem where the error message lived at the top of a tall modal and
 * never scrolled into view.
 *
 * Behaviour:
 *  - Auto-scrolls itself into view (smooth) the moment it appears.
 *  - Bright rose ring + pulsing icon so it stands out against the dark UI.
 *  - Optional onDismiss to allow clearing without re-submitting.
 *
 * Usage:
 *   <FormError message={err} onDismiss={() => setErr("")} />
 *   <button onClick={save}>Save</button>
 */
export default function FormError({ message, onDismiss, className = "", testId = "form-error" }) {
  const ref = useRef(null);

  // Scroll into view whenever a new error arrives so the user can't miss it.
  useEffect(() => {
    if (!message || !ref.current) return;
    try {
      ref.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    } catch (_e) { /* no-op for older browsers */ }
  }, [message]);

  if (!message) return null;

  return (
    <div
      ref={ref}
      className={`flex items-start gap-2 px-3 py-2.5 rounded-md bg-rose-500/15 border-2 border-rose-500/60 text-rose-100 text-sm shadow-[0_0_18px_-4px_rgba(244,63,94,0.5)] fade-up ${className}`}
      role="alert"
      aria-live="assertive"
      data-testid={testId}
    >
      <AlertCircle size={16} className="shrink-0 text-rose-300 mt-0.5" />
      <div className="flex-1 leading-snug">
        <div className="text-[10px] uppercase tracking-widest font-mono-data text-rose-300 mb-0.5">Error</div>
        <div className="text-rose-50">{message}</div>
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 p-1 rounded text-rose-300 hover:text-rose-100 hover:bg-rose-500/20"
          aria-label="Dismiss error"
          data-testid={`${testId}-dismiss`}
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
}
